/* eslint-disable max-len */
import { projectDomainName } from 'shared/constants'
import { languagesType } from 'shared/locale'
import { BonusName } from 'shared/models/Promotion'

type IBonusCardInfo = Record<
  languagesType,
  {
    firstFreeSpins: (freeSpinCount: string, wagerCoefficient?: string, wagerMinutesPeriod?: number) => string
    everyFreeSpins: (freeSpinCount: string, wagerCoefficient?: string, wagerMinutesPeriod?: number) => string
    firstDeposit: (depositBonus: string, wagerCoefficient?: string, wagerMinutesPeriod?: number) => string
    everyDeposit: (depositBonus: string, wagerCoefficient?: string, wagerMinutesPeriod?: number) => string
    register: (freeSpinCount: string, wagerCoefficient?: string, wagerMinutesPeriod?: number) => string
  }
>

const bonusCardInfo: IBonusCardInfo = {
  'en-US': {
    firstFreeSpins: (freeSpinCount: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        At ${projectDomainName}, the <span style="color: #FBFE01">${freeSpinCount} no-deposit free spins</span>
        are permanently available to all players!
        This Free Spins bonus applies to every player's deposit.
        A free spin is a spin in slots where no money is deducted from the player's balance.
        However, it is important to observe the roll-over rule:
        the amount won in the Free Spins is multiplied by
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        making it your goal to be achieved within ${wagerMinutesPeriod} days.
        To achieve this, simply make a deposit and continue playing.
        Fun is guaranteed!
      </span>
    `,

    everyFreeSpins: (freeSpinCount: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        At ${projectDomainName}, the <span style="color: #FBFE01">${freeSpinCount} no-deposit free spins</span>
        are permanently available to all players!
        This Free Spins bonus applies to every player's deposit.
        A free spin is a spin in slots where no money is deducted from the player's balance.
        However, it is important to observe the roll-over rule:
        the amount won in the Free Spins is multiplied by
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        making it your goal to be achieved within ${wagerMinutesPeriod} days.
        To achieve this, simply make a deposit and continue playing.
        Fun is guaranteed!
      </span>
    `,

    firstDeposit: (depositBonus: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        Here at ${projectDomainName}, <span style="color: #FBFE01">a first deposit bonus of ${depositBonus}%</span>
        is permanently available for all new players!
        This bonus applies only to the player's first deposit and is equal to
        <span style="color: #FBFE01">${depositBonus}%</span> of the deposited amount.
        Bonus money is real-money equivalent rewards that the user receives from the casino for free.
        However, it is important to observe the roll-over rule:
        the amount won in the bonus is multiplied by
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        making it your goal to be achieved within ${wagerMinutesPeriod} days.
        When withdrawing funds, the unused bonus is canceled!
      </span>
    `,

    everyDeposit: (depositBonus: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        Here at ${projectDomainName} <span style="color: #FBFE01">a Deposit Bonus of ${depositBonus}%</span>
        is permanently available for all players!
        This bonus applies to each deposit made by the player and is equal to
        <span style="color: #FBFE01">${depositBonus}%</span> of the deposited amount.
        Bonus money is real-money equivalent rewards that the user receives from the casino for free.
        However, it is important to observe the roll-over rule:
        the amount won in the bonus is multiplied by
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        making it your goal to be achieved within ${wagerMinutesPeriod} days.
        When withdrawing funds, the unused bonus is canceled!
      </span>
    `,

    register: (freeSpinCount: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        This bonus allows you to enjoy the slot games available under "open games"
        in your promotions area, where each spin will be deducted from the
        <span style="color: #FBFE01">${freeSpinCount} FREE SPINS</span>!
        Any balance earned during these plays can be converted into real balance,
        ready to be used in games or withdrawn.
        However, it is important to observe the roll-over rule:
        the amount won in the Free Spins is multiplied by
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        making it your goal to be achieved within ${wagerMinutesPeriod} days.
        To achieve this, simply make a deposit and continue playing.
        Fun is guaranteed!
      </span>
    `,
  },

  'es-ES': {
    firstFreeSpins: (freeSpinCount: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        ¡En ${projectDomainName}, los <span style="color: #FBFE01">${freeSpinCount} giros gratis sin depósito</span>
        están disponibles de forma permanente para todos los jugadores!
        Este bono de giros gratis se aplica a cada depósito del jugador.
        Un giro gratis es un giro en las tragamonedas en el que no se deduce dinero del saldo del jugador.
        Sin embargo, es importante observar la regla de reinversión:
        el monto ganado con los Giros Gratis se multiplica por
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        debiendo cumplirse dentro de ${wagerMinutesPeriod} días.
        ¡La diversión está garantizada!
      </span>
    `,

    everyFreeSpins: (freeSpinCount: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        ¡En ${projectDomainName}, los <span style="color: #FBFE01">${freeSpinCount} giros gratis sin depósito</span>
        están disponibles de forma permanente para todos los jugadores!
        Este bono de giros gratis se aplica a cada depósito del jugador.
        Un giro gratis es un giro en las tragamonedas en el que no se deduce dinero del saldo del jugador.
        Sin embargo, es importante observar la regla de reinversión:
        el monto ganado con los Giros Gratis se multiplica por
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        debiendo cumplirse dentro de ${wagerMinutesPeriod} días.
        ¡La diversión está garantizada!
      </span>
    `,

    firstDeposit: (depositBonus: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        Aquí en ${projectDomainName}, <span style="color: #FBFE01">un bono de primer depósito de ${depositBonus}%</span>
        está disponible permanentemente para todos los nuevos jugadores.
        Este bono se aplica únicamente al primer depósito del jugador y equivale a
        <span style="color: #FBFE01">${depositBonus}%</span> del monto depositado.
        El dinero del bono son recompensas equivalentes a dinero real que el usuario recibe del casino de forma gratuita.
        Sin embargo, es importante observar la regla de reinversión:
        el monto ganado con el bono se multiplica por
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        debiendo cumplirse dentro de ${wagerMinutesPeriod} días.
        ¡Al retirar fondos, el bono no utilizado se cancela!
      </span>
    `,

    everyDeposit: (depositBonus: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        Aquí en ${projectDomainName} <span style="color: #FBFE01">un Bono de Depósito de ${depositBonus}%</span>
        está disponible permanentemente para todos los jugadores!
        Este bono se aplica a cada depósito del jugador y equivale a
        <span style="color: #FBFE01">${depositBonus}%</span> del monto depositado.
        El dinero del bono son recompensas equivalentes a dinero real que el usuario recibe del casino de forma gratuita.
        Sin embargo, es importante observar la regla de reinversión:
        el monto ganado con el bono se multiplica por
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        debiendo cumplirse dentro de ${wagerMinutesPeriod} días.
        ¡Al retirar fondos, el bono no utilizado se cancela!
      </span>
    `,

    register: (freeSpinCount: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        Este bono te permite disfrutar de los juegos de tragamonedas disponibles en "juegos abiertos"
        en tu área de promociones, donde cada giro se deducirá de los
        <span style="color: #FBFE01">${freeSpinCount} GIROS GRATIS</span>!
        Cualquier saldo ganado durante estas jugadas se puede convertir en saldo real,
        listo para ser utilizado en juegos o retirado.
        Sin embargo, es importante observar la regla de reinversión:
        la cantidad ganada en los Giros Gratis se multiplica por
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        debiendo cumplirse dentro de ${wagerMinutesPeriod} días.
        Para lograrlo, simplemente haz un depósito y continúa jugando.
        ¡La diversión está garantizada!
      </span>
    `,
  },

  'pt-BR': {
    firstFreeSpins: (freeSpinCount: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        Aqui no ${projectDomainName} <span style="color: #FBFE01">${freeSpinCount} Giros Grátis</span> no Primeiro Depósito
        estão disponíveis permanentemente para todos os novos jogadores!
        Este bônus de Giros Grátis se aplica apenas ao primeiro depósito do jogador.
        Giros grátis são rodadas em slots onde nenhum dinheiro é deduzido do saldo do jogador.
        No entanto, é importante observar a regra de rollover:
        o valor ganho com as Rodadas Grátis é multiplicado por
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        devendo ser cumprido em até ${wagerMinutesPeriod} dias.
        A diversão é garantida!
      </span>
    `,

    everyFreeSpins: (freeSpinCount: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        Aqui no ${projectDomainName} <span style="color: #FBFE01">${freeSpinCount} Giros Grátis</span> no Depósito
        estão disponíveis permanentemente para todos os jogadores!
        Este bônus de Giros Grátis se aplica a cada depósito do jogador.
        Giros grátis são rodadas em slots onde nenhum dinheiro é deduzido do saldo do jogador.
        No entanto, é importante observar a regra de rollover:
        o valor ganho com as Rodadas Grátis é multiplicado por
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        devendo ser cumprido em até ${wagerMinutesPeriod} dias.
        A diversão é garantida!
      </span>
    `,

    firstDeposit: (depositBonus: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        Aqui no ${projectDomainName}, <span style="color: #FBFE01">um Bônus de Primeiro Depósito de ${depositBonus}%</span>
        está disponível permanentemente para todos os novos jogadores!
        Este bônus se aplica apenas ao primeiro depósito do jogador e é igual a
        <span style="color: #FBFE01">${depositBonus}%</span> do valor depositado.
        O dinheiro do bônus são recompensas equivalentes a dinheiro real.
        No entanto, é importante observar a regra de rollover:
        o valor ganho com o bônus é multiplicado por
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        devendo ser cumprido em até ${wagerMinutesPeriod} dias.
        Ao sacar, o bônus não utilizado será cancelado!
      </span>
    `,

    everyDeposit: (depositBonus: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        Aqui no ${projectDomainName} <span style="color: #FBFE01">um Bônus de Depósito de ${depositBonus}%</span>
        está disponível permanentemente para todos os jogadores!
        Este bônus se aplica a cada depósito do jogador e é igual a
        <span style="color: #FBFE01">${depositBonus}%</span> do valor depositado.
        O dinheiro do bônus são recompensas equivalentes a dinheiro real.
        No entanto, é importante observar a regra de rollover:
        o valor ganho com o bônus é multiplicado por
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        devendo ser cumprido em até ${wagerMinutesPeriod} dias.
        Ao sacar, o bônus não utilizado será cancelado!
      </span>
    `,

    register: (freeSpinCount: string, wagerCoefficient = '', wagerMinutesPeriod = 7) => `
      <span>
        Este bônus permite que você desfrute dos jogos de slot disponíveis em “jogos abertos”
        na sua área de promoções, onde cada giro será deduzido dos
        <span style="color: #FBFE01">${freeSpinCount} GIROS GRÁTIS</span>!
        Qualquer saldo ganho durante essas jogadas pode ser convertido em saldo real,
        pronto para ser usado em jogos ou sacado.
        Contudo, é importante observar a regra de rollover:
        o valor ganho nas Rodadas Grátis é multiplicado por
        <span style="color: #FBFE01">${wagerCoefficient}</span>,
        devendo ser cumprido em até ${wagerMinutesPeriod} dias.
        Para isso, basta fazer um depósito e continuar jogando.
        A diversão está garantida!
      </span>
    `,
  },
}

export const getBonusCardFreeSpinsInfo = (
  lang: languagesType,
  freeSpins: number,
  promotioName: string,
  wagerCoefficient?: number,
  wagerMinutesPeriod = 7,
) => {
  switch (promotioName) {
    case BonusName.firstDepositFreeSpins:
      return {
        __html: bonusCardInfo[lang].firstFreeSpins(
          String(freeSpins),
          String(wagerCoefficient ?? ''),
          wagerMinutesPeriod,
        ),
      }
    case BonusName.everyDepositFreeSpins:
      return {
        __html: bonusCardInfo[lang].everyFreeSpins(
          String(freeSpins),
          String(wagerCoefficient ?? ''),
          wagerMinutesPeriod,
        ),
      }
    case BonusName.register:
      return {
        __html: bonusCardInfo[lang].register(String(freeSpins), String(wagerCoefficient ?? ''), wagerMinutesPeriod),
      }
    default:
      return {
        __html: bonusCardInfo[lang].firstFreeSpins(
          String(freeSpins),
          String(wagerCoefficient ?? ''),
          wagerMinutesPeriod,
        ),
      }
  }
}

export const getBonusCardFirstDepositInfo = (
  lang: languagesType,
  depositCoef: number,
  promotioName: string,
  wagerCoefficient?: number,
  wagerMinutesPeriod = 7,
) => {
  switch (promotioName) {
    case BonusName.firstDepositBonus:
      return {
        __html: bonusCardInfo[lang].firstDeposit(
          String((depositCoef ?? 1) * 100),
          String(wagerCoefficient ?? ''),
          wagerMinutesPeriod,
        ),
      }
    case BonusName.everyDepositBonus:
      return {
        __html: bonusCardInfo[lang].everyDeposit(
          String((depositCoef ?? 1) * 100),
          String(wagerCoefficient ?? ''),
          wagerMinutesPeriod,
        ),
      }
    default:
      return {
        __html: bonusCardInfo[lang].firstDeposit(
          String((depositCoef ?? 1) * 100),
          String(wagerCoefficient ?? ''),
          wagerMinutesPeriod,
        ),
      }
  }
}
